import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Gallery } from '../../types/Gallery';
import { Category, loadCategories } from '../../utils/categories';
import { getOptimizedImageUrl } from '../../utils/CloudinaryAPI';
import './GalleryModal.css';

interface GalleryModalProps {
  gallery: Gallery | null;
  isOpen: boolean;
  onClose: () => void;
}

// Removed getCategoryEmoji function - no icons needed

const GalleryModal: React.FC<GalleryModalProps> = ({ gallery, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const loadCategoriesData = async () => {
    try {
      const { loadLiveCategories } = await import('../../utils/CategoryAPI');
      const serverCategories = await loadLiveCategories();
      if (Array.isArray(serverCategories) && serverCategories.length > 0) {
        setCategories(serverCategories);
        return;
      }
    } catch (error) {
      console.log('Server categories not available, using local fallback');
    }
    
    // Fallback to local categories
    const localCategories = loadCategories();
    setCategories(localCategories);
  };

  const getCategoryColor = (categoryName: string): string => {
    const category = categories.find(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase() || 
      cat.slug.toLowerCase() === categoryName.toLowerCase()
    );
    return category?.color || '#8B4513'; // Default fallback color
  };

  useEffect(() => {
    // Load categories initially
    loadCategoriesData();

    // Listen for category updates from admin panel
    const handleCategoryUpdate = () => {
      console.log('🔄 GalleryModal: Category update detected, reloading categories...');
      loadCategoriesData();
    };

    // Listen for storage events (when categories are updated in admin panel)
    const handleStorageChange = (e: StorageEvent) => {
      // Only reload if admin_categories key changed
      if (e.key === 'admin_categories') {
        console.log('🔄 GalleryModal: admin_categories localStorage changed, reloading categories...');
        loadCategoriesData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom category update events
    window.addEventListener('category_updated', handleCategoryUpdate);
    window.addEventListener('categories_updated', handleCategoryUpdate);

    // Cleanup listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('category_updated', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Reset state when gallery changes
  useEffect(() => {
    if (gallery) {
      setCurrentImageIndex(0);
      setImageError(false);
    }
  }, [gallery]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen || !gallery) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          setCurrentImageIndex(prevIndex => 
            prevIndex === 0 ? (gallery.images.length - 1) : prevIndex - 1
          );
          break;
        case 'ArrowRight':
          setCurrentImageIndex(prevIndex => 
            prevIndex === (gallery.images.length - 1) ? 0 : prevIndex + 1
          );
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, gallery, onClose]);

  if (!isOpen || !gallery) return null;

  const currentImage = gallery.images[currentImageIndex];
  const hasImages = gallery.images.length > 0;

  const modalContent = (
    <div className={`gallery-modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      {/* Gallery Header - Top Center Positioning */}
      <div className="gallery-modal-header" style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        background: 'rgba(248, 250, 252, 0.95)',
        padding: '15px 25px',
        borderRadius: '50px',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        backdropFilter: 'blur(10px)',
        maxWidth: '500px',
        minWidth: '250px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            color: '#1a202c',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            {gallery.name}
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {gallery.order && (
              <span style={{
                fontSize: '0.8rem',
                padding: '4px 8px',
                background: 'rgba(139, 69, 19, 0.1)',
                borderRadius: '12px',
                color: '#8B4513',
                fontWeight: '600'
              }}>
                #{gallery.order}
              </span>
            )}
            <span style={{
              fontSize: '0.8rem',
              padding: '4px 8px',
              background: getCategoryColor(gallery.category),
              borderRadius: '12px',
              color: 'white'
            }}>
              {gallery.category.charAt(0).toUpperCase() + gallery.category.slice(1)}
            </span>
            <span style={{
              fontSize: '0.8rem',
              color: '#6b7280'
            }}>
              {gallery.images.length} images
            </span>
            {hasImages && (
              <span style={{
                fontSize: '0.8rem',
                color: '#6b7280'
              }}>
                {currentImageIndex + 1} / {gallery.images.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Close Button - Top Right */}
      <div className="modal-close-button" style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 100003,
        display: 'block'
      }}>
        <button 
          onClick={() => {
            console.log('🎯 Close button clicked!');
            onClose();
          }}
          className="gallery-close-btn-theme"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      {/* Main Gallery Modal */}
      <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="image-viewer">
            {hasImages ? (
              <>
                {imageError ? (
                  <div className="image-error-placeholder">
                    <span>Error loading image.</span>
                  </div>
                ) : (
                  <div className="current-image-container">
                    <img
                      src={currentImage.url || getOptimizedImageUrl(currentImage.publicId, 'modal')}
                      alt={currentImage.alt || gallery.name}
                      className="main-image"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '85vh', 
                        objectFit: 'contain',
                        display: 'block',
                        borderRadius: '12px'
                      }}
                      onError={(e) => {
                        setImageError(true);
                      }}
                      onLoad={() => {
                        // Image loaded successfully
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="no-images-placeholder">
                <span>No images in this gallery.</span>
              </div>
            )}

            {/* Navigation Arrows with Preview Images */}
            {gallery.images.length > 1 && (
              <>
                <div className="side-navigation left-nav">
                  <button 
                    className="preview-btn" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setCurrentImageIndex(prevIndex => 
                        prevIndex === 0 ? (gallery.images.length - 1) : prevIndex - 1
                      ); 
                    }}
                  >
                    <div className="preview-container">
                      <img 
                        src={gallery.images[currentImageIndex === 0 ? gallery.images.length - 1 : currentImageIndex - 1].url}
                        alt="Previous"
                        className="preview-thumbnail"
                      />
                      <i className="fas fa-chevron-left preview-arrow"></i>
                    </div>
                  </button>
                </div>
                <div className="side-navigation right-nav">
                  <button 
                    className="preview-btn" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setCurrentImageIndex(prevIndex => 
                        prevIndex === (gallery.images.length - 1) ? 0 : prevIndex + 1
                      ); 
                    }}
                  >
                    <div className="preview-container">
                      <img 
                        src={gallery.images[currentImageIndex === gallery.images.length - 1 ? 0 : currentImageIndex + 1].url}
                        alt="Next"
                        className="preview-thumbnail"
                      />
                      <i className="fas fa-chevron-right preview-arrow"></i>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal in a portal to ensure it appears above all other elements
  return createPortal(modalContent, document.body);
};

export default GalleryModal;